import { TRPCClientError } from "@trpc/client";
import { TRPCError } from "@trpc/server";
import { ZodError } from "zod";

const UNEXPECTED_TRPC_ERROR_MESSAGE =
  "Something went wrong. Please try again or contact engineering.";

type ZodIssueLike = {
  path?: unknown;
  message?: unknown;
};

export const formatZodIssues = (issues: ZodIssueLike[]): string =>
  issues
    .map((issue) => {
      if (
        !issue ||
        typeof issue !== "object" ||
        typeof issue.message !== "string"
      ) {
        return null;
      }

      const path = Array.isArray(issue.path)
        ? issue.path
            .map((segment) => String(segment))
            .filter((segment) => segment.length > 0)
            .join(".")
        : "";

      return path.length > 0 ? `${path}: ${issue.message}` : issue.message;
    })
    .filter((issueMessage): issueMessage is string => Boolean(issueMessage))
    .join("; ");

const formatZodIssuesMessage = (message: string): string | null => {
  try {
    const parsed: unknown = JSON.parse(message);
    if (!Array.isArray(parsed)) {
      return null;
    }

    const formatted = formatZodIssues(parsed);
    return formatted.length > 0 ? formatted : null;
  } catch {
    return null;
  }
};

const getRawTrpcErrorMessage = (error: unknown): string | null => {
  if (error instanceof ZodError) {
    return error.issues.map((issue) => issue.message).join("; ");
  }

  if (error instanceof TRPCError || error instanceof TRPCClientError) {
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return null;
};

const isInternalServerFault = (error: unknown): boolean => {
  if (error instanceof TRPCError) {
    return error.code === "INTERNAL_SERVER_ERROR";
  }

  if (error instanceof TRPCClientError) {
    return error.data?.code === "INTERNAL_SERVER_ERROR";
  }

  return true;
};

export const getTrpcErrorMessage = (error: unknown): string => {
  if (error instanceof ZodError) {
    return error.issues.map((issue) => issue.message).join("; ");
  }

  const rawMessage = getRawTrpcErrorMessage(error);

  if (rawMessage) {
    const zodMessage = formatZodIssuesMessage(rawMessage);
    if (zodMessage) {
      return zodMessage;
    }

    if (!isInternalServerFault(error)) {
      return rawMessage;
    }
  }

  return UNEXPECTED_TRPC_ERROR_MESSAGE;
};

export const shouldSuppressTrpcErrorToast = (meta: unknown): boolean =>
  Boolean(
    meta &&
    typeof meta === "object" &&
    "suppressTrpcErrorToast" in meta &&
    meta.suppressTrpcErrorToast,
  );
